import React, { useState, useEffect, useCallback } from 'react';
import { Activity, AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp, RefreshCw, Eye, EyeOff } from 'lucide-react';

/**
 * UniverseWatchtower - Admin overlay for monitoring guardians
 * 
 * Features:
 * - Polls /api/admin/guardians/metrics and /api/admin/guardians/alerts
 * - Shows status pill: OK / Degraded / Critical
 * - Displays latest alerts with severity
 * - Shows telemetry counters (referral, constellation, moderation)
 * - Collapsible (default: collapsed for non-admin, expanded for admin)
 * - Failure tolerant (shows "unavailable" on API errors)
 */

const POLL_INTERVAL_MS = 20000; // 20 seconds
const API_BASE = window.location.origin;

// Telemetry fields to display
const TELEMETRY_FIELDS = [
  { key: 'referral_commissions_created', label: 'Referrals Created', color: 'text-emerald-400' },
  { key: 'referral_skipped', label: 'Referrals Skipped', color: 'text-amber-400' },
  { key: 'referral_duplicates_blocked', label: 'Duplicates Blocked', color: 'text-blue-400' },
  { key: 'referral_reversals', label: 'Reversals', color: 'text-red-400' },
  { key: 'constellation_edges', label: 'Constellation Edges', color: 'text-purple-400' },
  { key: 'constellation_profiles', label: 'Profiles', color: 'text-indigo-400' },
  { key: 'moderation_queue_depth', label: 'Moderation Queue', color: 'text-orange-400' },
];

export const UniverseWatchtower = ({ 
  isAdmin = false,
  defaultExpanded = null, // null = auto (expanded for admin)
  showForNonAdmin = false, // Only show when degraded/critical
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded ?? isAdmin);
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [status, setStatus] = useState('unknown'); // ok | degraded | critical | unavailable
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Get auth token
  const getToken = useCallback(() => {
    try {
      return localStorage.getItem('token');
    } catch {
      return null;
    }
  }, []);

  // Fetch metrics and alerts
  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError('No auth token');
      setStatus('unavailable');
      setLoading(false);
      return;
    }

    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Fetch metrics
      const metricsRes = await fetch(`${API_BASE}/api/admin/guardians/metrics`, { headers });
      
      if (metricsRes.status === 403 || metricsRes.status === 401) {
        // Not admin or unauthorized
        setError('Admin access required');
        setStatus('unavailable');
        setLoading(false);
        return;
      }
      
      if (!metricsRes.ok) {
        throw new Error(`Metrics API returned ${metricsRes.status}`);
      }
      
      const metricsData = await metricsRes.json();
      setMetrics(metricsData);

      // Fetch alerts
      const alertsRes = await fetch(`${API_BASE}/api/admin/guardians/alerts?limit=5`, { headers });
      
      let fetchedAlerts = [];
      if (alertsRes.ok) {
        const alertsData = await alertsRes.json();
        fetchedAlerts = alertsData.alerts || alertsData || [];
        setAlerts(fetchedAlerts);
      } else {
        // Alerts endpoint might not exist - that's OK
        setAlerts([]);
      }

      // Fetch realtime SSE stats
      try {
        const rtRes = await fetch(`${API_BASE}/api/admin/realtime/sse/stats`, { headers });
        if (rtRes.ok) {
          const rtData = await rtRes.json();
          setRealtimeStats(rtData);
        } else {
          setRealtimeStats(null);
        }
      } catch {
        setRealtimeStats(null);
      }

      // Determine status based on freshly fetched alerts (not stale state)
      const criticalCount = Array.isArray(fetchedAlerts) 
        ? fetchedAlerts.filter(a => a.severity === 'CRITICAL' || a.severity === 'ERROR').length
        : 0;
      
      const warningCount = Array.isArray(fetchedAlerts)
        ? fetchedAlerts.filter(a => a.severity === 'WARNING').length
        : 0;
      
      if (criticalCount > 0) {
        setStatus('critical');
      } else if (warningCount > 0) {
        setStatus('degraded');
      } else {
        setStatus('ok');
      }

      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      console.warn('[Watchtower] Failed to fetch data:', err);
      setError(err.message || 'Failed to fetch');
      setStatus('unavailable');
    } finally {
      setLoading(false);
    }
  }, [getToken]); // Removed alerts dependency - status is computed from fetched data, not state

  // Poll on interval
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Don't render for non-admin unless showForNonAdmin is true and status is bad
  if (!isAdmin) {
    if (!showForNonAdmin) return null;
    if (status !== 'degraded' && status !== 'critical') return null;
  }

  // Status pill styling
  const getStatusStyles = () => {
    switch (status) {
      case 'ok':
        return { bg: 'bg-emerald-500/20', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: CheckCircle };
      case 'degraded':
        return { bg: 'bg-amber-500/20', border: 'border-amber-500/30', text: 'text-amber-400', icon: AlertTriangle };
      case 'critical':
        return { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400', icon: XCircle };
      default:
        return { bg: 'bg-gray-500/20', border: 'border-gray-500/30', text: 'text-gray-400', icon: Activity };
    }
  };

  const statusStyle = getStatusStyles();
  const StatusIcon = statusStyle.icon;

  return (
    <div 
      className={`fixed top-4 right-4 z-50 ${statusStyle.bg} ${statusStyle.border} border rounded-lg shadow-lg backdrop-blur-sm transition-all duration-200`}
      style={{ maxWidth: '320px', minWidth: expanded ? '280px' : '120px' }}
      data-testid="universe-watchtower"
    >
      {/* Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium"
        data-testid="watchtower-toggle"
      >
        <StatusIcon className={`w-4 h-4 ${statusStyle.text}`} />
        <span className={statusStyle.text}>
          {status === 'unavailable' ? 'Watchtower' : status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
        <div className="flex-1" />
        {loading && <RefreshCw className="w-3 h-3 text-gray-500 animate-spin" />}
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-700/50 px-3 py-2 space-y-3">
          {/* Error state */}
          {error && (
            <div className="text-xs text-red-400">
              {error}
            </div>
          )}

          {/* Alerts section */}
          {alerts.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 mb-1 font-medium">Recent Alerts</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {alerts.slice(0, 5).map((alert, idx) => (
                  <div 
                    key={idx}
                    className={`text-xs p-1.5 rounded ${
                      alert.severity === 'ERROR' || alert.severity === 'CRITICAL' 
                        ? 'bg-red-500/10 text-red-300'
                        : alert.severity === 'WARNING'
                          ? 'bg-amber-500/10 text-amber-300'
                          : 'bg-gray-500/10 text-gray-300'
                    }`}
                  >
                    <div className="font-medium">{alert.agent}</div>
                    <div className="text-[10px] opacity-80 truncate">{alert.message}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Telemetry counters */}
          {metrics && (
            <div>
              <div className="text-xs text-gray-500 mb-1 font-medium">Telemetry</div>
              <div className="grid grid-cols-2 gap-1 text-[10px]">
                {TELEMETRY_FIELDS.map(field => {
                  // Find the value in metrics.telemetry (new structure)
                  const telemetry = metrics.telemetry || {};
                  let value = telemetry[field.key];
                  if (value === undefined) value = 0;
                  
                  return (
                    <div key={field.key} className="flex justify-between">
                      <span className="text-gray-500">{field.label}:</span>
                      <span className={field.color}>{value.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Realtime SSE Stats */}
          {realtimeStats && (
            <div>
              <div className="text-xs text-gray-500 mb-1 font-medium">Realtime (SSE)</div>
              <div className="space-y-2 text-[10px]">
                <div className="bg-gray-500/10 rounded p-2">
                  <div className="font-medium text-gray-300">Chat SSE</div>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    <div className="text-gray-400">Rooms</div>
                    <div className="text-gray-200 text-right">{realtimeStats.chat?.active_rooms ?? '-'}</div>
                    <div className="text-gray-400">Connections</div>
                    <div className="text-gray-200 text-right">{realtimeStats.chat?.total_connections ?? '-'}</div>
                    <div className="text-gray-400">Global Tracked</div>
                    <div className="text-gray-200 text-right">{realtimeStats.chat?.global_tracked ?? '-'}</div>
                    <div className="text-gray-400">Cap (user/global)</div>
                    <div className="text-gray-200 text-right">
                      {realtimeStats.chat?.caps?.per_user ?? '-'} / {realtimeStats.chat?.caps?.global ?? '-'}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-500/10 rounded p-2">
                  <div className="font-medium text-gray-300">Lounge SSE</div>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    <div className="text-gray-400">Global Tracked</div>
                    <div className="text-gray-200 text-right">{realtimeStats.lounge?.global_tracked ?? '-'}</div>
                    <div className="text-gray-400">Cap (user/global)</div>
                    <div className="text-gray-200 text-right">
                      {realtimeStats.lounge?.caps?.per_user ?? '-'} / {realtimeStats.lounge?.caps?.global ?? '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Last update */}
          {lastUpdate && (
            <div className="text-[10px] text-gray-600 flex items-center gap-1">
              <Activity className="w-3 h-3" />
              Updated {lastUpdate.toLocaleTimeString()}
            </div>
          )}

          {/* Manual refresh */}
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="w-full text-xs text-gray-500 hover:text-gray-300 flex items-center justify-center gap-1 py-1"
            disabled={loading}
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      )}
    </div>
  );
};

export default UniverseWatchtower;
